(() => {
  const CollectionAjaxExperience = customElements.get('collection-ajax-experience');
  if (!CollectionAjaxExperience) return;

  const prototype = CollectionAjaxExperience.prototype;
  if (prototype.cardologyResponsiveFiltersPatched) return;
  prototype.cardologyResponsiveFiltersPatched = true;

  const MOBILE_BREAKPOINT = 768;
  const MOBILE_PREVIEW_DELAY = 320;

  const originalConnectedCallback = prototype.connectedCallback;
  const originalDisconnectedCallback = prototype.disconnectedCallback;
  const originalHandleClick = prototype.handleClick;
  const originalHandleChange = prototype.handleChange;
  const originalFetchAndReplace = prototype.fetchAndReplace;

  prototype.connectedCallback = function connectedCallback() {
    originalConnectedCallback.call(this);
    this.setupResponsiveFilters();
  };

  prototype.disconnectedCallback = function disconnectedCallback() {
    if (this.responsiveFilterViewportHandler) {
      window.removeEventListener('resize', this.responsiveFilterViewportHandler);
      window.removeEventListener('scroll', this.responsiveFilterViewportHandler, true);
    }

    if (this.mobileFilterInputHandler) {
      this.removeEventListener('input', this.mobileFilterInputHandler);
    }

    window.clearTimeout(this.mobileFilterPreviewTimer);
    this.mobileFilterPreviewController?.abort();

    if (typeof originalDisconnectedCallback === 'function') {
      originalDisconnectedCallback.call(this);
    }
  };

  prototype.setupResponsiveFilters = function setupResponsiveFilters() {
    if (!this.responsiveFiltersInitialized) {
      this.responsiveFiltersInitialized = true;
      this.responsiveFilterViewportHandler = () => this.positionOpenDesktopFilter();
      this.mobileFilterInputHandler = (event) => {
        const control = event.target.closest('[data-mobile-filter-drawer] [data-collection-filter]');
        if (!control || control.type !== 'number') return;
        this.handleMobileFilterDraftChange(control);
      };

      window.addEventListener('resize', this.responsiveFilterViewportHandler, { passive: true });
      window.addEventListener('scroll', this.responsiveFilterViewportHandler, { passive: true, capture: true });
      this.addEventListener('input', this.mobileFilterInputHandler);
    }

    this.bindDesktopFilterDropdowns();
    this.updateMobileFilterCount();
    this.renderMobileSelectedFilters();
    this.updateMobileApplyButton(this.getRenderedResultCount());
  };

  prototype.bindDesktopFilterDropdowns = function bindDesktopFilterDropdowns() {
    this.querySelectorAll('[data-filter-dropdown]').forEach((details) => {
      if (details.dataset.responsiveFilterBound === 'true') return;
      details.dataset.responsiveFilterBound = 'true';

      details.addEventListener('toggle', () => {
        if (!details.open || window.innerWidth < MOBILE_BREAKPOINT) return;

        this.querySelectorAll('[data-filter-dropdown][open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });

        requestAnimationFrame(() => this.positionDesktopFilter(details));
      });
    });

    const rail = this.querySelector('[data-filter-rail]');
    if (rail && rail.dataset.responsiveFilterBound !== 'true') {
      rail.dataset.responsiveFilterBound = 'true';
      rail.addEventListener('scroll', () => {
        this.querySelectorAll('[data-filter-dropdown][open]').forEach((details) => {
          details.removeAttribute('open');
        });
      }, { passive: true });
    }
  };

  prototype.positionDesktopFilter = function positionDesktopFilter(details) {
    if (!details?.open || window.innerWidth < MOBILE_BREAKPOINT) return;

    const trigger = details.querySelector(':scope > summary');
    const panel = details.querySelector(':scope > .cae-filter__panel');
    if (!trigger || !panel) return;

    const triggerRect = trigger.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredWidth = panel.classList.contains('cae-filter__panel--more')
      ? Math.min(520, window.innerWidth - (viewportPadding * 2))
      : Math.min(260, window.innerWidth - (viewportPadding * 2));

    let left = triggerRect.left;
    if (left + preferredWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - preferredWidth - viewportPadding;
    }
    left = Math.max(viewportPadding, left);

    panel.style.setProperty('--cae-panel-top', `${Math.round(triggerRect.bottom + 10)}px`);
    panel.style.setProperty('--cae-panel-left', `${Math.round(left)}px`);
    panel.style.setProperty('--cae-panel-width', `${Math.round(preferredWidth)}px`);
  };

  prototype.positionOpenDesktopFilter = function positionOpenDesktopFilter() {
    if (window.innerWidth < MOBILE_BREAKPOINT) return;
    const openFilter = this.querySelector('[data-filter-dropdown][open]');
    if (openFilter) this.positionDesktopFilter(openFilter);
  };

  prototype.paramsFromControls = function paramsFromControls(scope = null) {
    const params = this.clearFilterParams();
    const root = scope || this;
    const controls = [...root.querySelectorAll('[data-collection-filter]')]
      .filter((control) => scope || !control.closest('[data-mobile-filter-drawer]'));

    controls.forEach((control) => {
      if (!control.name || control.disabled) return;

      if (control.type === 'checkbox') {
        if (control.checked) params.append(control.name, control.value);
        return;
      }

      if (control.value !== '') params.set(control.name, control.value);
    });

    params.delete('page');
    return params;
  };

  prototype.getRenderedResultCount = function getRenderedResultCount(root = this) {
    const products = root.querySelector('.cae-products[data-results-count]');
    const count = Number.parseInt(products?.dataset.resultsCount || '', 10);
    return Number.isFinite(count) ? count : null;
  };

  prototype.captureMobileDrawerState = function captureMobileDrawerState(drawer) {
    const drawerBody = drawer.querySelector('.cae-drawer__body');
    const countNode = drawer.querySelector('[data-mobile-filter-count]');

    this.mobileCommittedState = {
      params: new URLSearchParams(this.currentParams()),
      publicUrl: new URL(window.location.href),
      drawerBodyHtml: drawerBody?.innerHTML || '',
      countText: countNode?.textContent || '',
      products: this.querySelector('.cae-products')?.cloneNode(true) || null,
      activeFilters: this.querySelector('[data-active-filters]')?.cloneNode(true) || null,
      desktopFilters: this.querySelector('[data-desktop-filters]')?.cloneNode(true) || null,
      mobileTriggerHtml: this.querySelector('[data-mobile-filter-open]')?.innerHTML || ''
    };

    this.mobilePreviewParams = new URLSearchParams(this.mobileCommittedState.params);
    this.mobilePreviewPublicUrl = new URL(this.mobileCommittedState.publicUrl);
    this.mobilePreviewResultCount = this.getRenderedResultCount();
    this.mobilePreviewReady = true;
    this.mobilePreviewError = false;
  };

  prototype.restoreMobileCommittedState = function restoreMobileCommittedState() {
    const state = this.mobileCommittedState;
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!state || !drawer) return;

    const drawerBody = drawer.querySelector('.cae-drawer__body');
    if (drawerBody) drawerBody.innerHTML = state.drawerBodyHtml;

    const countNode = drawer.querySelector('[data-mobile-filter-count]');
    if (countNode) countNode.textContent = state.countText;

    const currentProducts = this.querySelector('.cae-products');
    if (currentProducts && state.products) currentProducts.replaceWith(state.products.cloneNode(true));

    const currentActiveFilters = this.querySelector('[data-active-filters]');
    if (currentActiveFilters && state.activeFilters) {
      currentActiveFilters.replaceWith(state.activeFilters.cloneNode(true));
    }

    const currentDesktopFilters = this.querySelector('[data-desktop-filters]');
    if (currentDesktopFilters && state.desktopFilters) {
      currentDesktopFilters.replaceWith(state.desktopFilters.cloneNode(true));
    }

    const mobileTrigger = this.querySelector('[data-mobile-filter-open]');
    if (mobileTrigger) mobileTrigger.innerHTML = state.mobileTriggerHtml;

    this.mobilePreviewParams = new URLSearchParams(state.params);
    this.mobilePreviewPublicUrl = new URL(state.publicUrl);
    this.mobilePreviewResultCount = this.getRenderedResultCount();
    this.mobilePreviewReady = true;
    this.mobilePreviewError = false;

    this.bindDesktopFilterDropdowns();
    this.updateMobileFilterCount(drawer);
    this.renderMobileSelectedFilters(drawer);
    this.updateMobileApplyButton(this.mobilePreviewResultCount);
  };

  prototype.clearMobileDrawerControls = function clearMobileDrawerControls() {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!drawer) return;

    drawer.querySelectorAll('[data-collection-filter]').forEach((control) => {
      if (control.type === 'checkbox') control.checked = false;
      else control.value = '';
    });

    this.updateMobileFilterCount(drawer);
    this.renderMobileSelectedFilters(drawer);
    this.scheduleMobileFilterPreview();
  };

  prototype.updateMobileFilterCount = function updateMobileFilterCount(drawer = null) {
    const activeDrawer = drawer || this.querySelector('[data-mobile-filter-drawer]');
    if (!activeDrawer) return;

    const checkedCount = activeDrawer.querySelectorAll('[data-collection-filter][type="checkbox"]:checked').length;
    const hasPrice = [...activeDrawer.querySelectorAll('[data-collection-filter][type="number"]')]
      .some((control) => control.value !== '');
    const count = checkedCount + (hasPrice ? 1 : 0);
    const countNode = activeDrawer.querySelector('[data-mobile-filter-count]');

    if (countNode) countNode.textContent = count > 0 ? ` (${count})` : '';
  };

  prototype.renderMobileSelectedFilters = function renderMobileSelectedFilters(drawer = null) {
    const activeDrawer = drawer || this.querySelector('[data-mobile-filter-drawer]');
    const container = activeDrawer?.querySelector('[data-mobile-selected-filters]');
    if (!activeDrawer || !container) return;

    container.replaceChildren();

    activeDrawer.querySelectorAll('[data-collection-filter][type="checkbox"]:checked').forEach((control) => {
      const label = control.dataset.filterLabel
        || control.closest('label')?.querySelector('span')?.textContent?.trim()
        || control.value;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cae-drawer__selected-chip';
      button.dataset.mobileSelectedRemove = 'value';
      button.dataset.filterParam = control.name;
      button.dataset.filterValue = control.value;
      button.setAttribute('aria-label', `Remove ${label} filter`);

      const text = document.createElement('span');
      text.textContent = label;
      const close = document.createElement('span');
      close.setAttribute('aria-hidden', 'true');
      close.textContent = 'x';
      button.append(text, close);
      container.append(button);
    });

    const priceControls = [...activeDrawer.querySelectorAll('[data-collection-filter][type="number"]')];
    if (priceControls.some((control) => control.value !== '')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cae-drawer__selected-chip';
      button.dataset.mobileSelectedRemove = 'price';
      button.setAttribute('aria-label', 'Remove price filter');

      const text = document.createElement('span');
      text.textContent = 'Price';
      const close = document.createElement('span');
      close.setAttribute('aria-hidden', 'true');
      close.textContent = 'x';
      button.append(text, close);
      container.append(button);
    }

    container.hidden = container.childElementCount === 0;
  };

  prototype.updateMobileApplyButton = function updateMobileApplyButton(count, options = {}) {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    const button = drawer?.querySelector('[data-mobile-filter-apply]');
    if (!button) return;

    const { loading = false, error = false } = options;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-live', 'polite');

    if (loading) {
      button.disabled = true;
      button.textContent = 'Updating cards...';
      return;
    }

    if (error) {
      button.disabled = false;
      button.textContent = 'Try Again';
      return;
    }

    if (count === 0) {
      button.disabled = true;
      button.textContent = 'No Matching Cards';
      return;
    }

    button.disabled = false;
    if (Number.isFinite(count)) {
      button.textContent = `View ${count} ${count === 1 ? 'Card' : 'Cards'}`;
    } else {
      button.textContent = 'View Results';
    }
  };

  prototype.handleMobileFilterDraftChange = function handleMobileFilterDraftChange(control) {
    const drawer = control.closest('[data-mobile-filter-drawer]');
    if (!drawer) return;

    this.updateMobileFilterCount(drawer);
    this.renderMobileSelectedFilters(drawer);
    this.scheduleMobileFilterPreview();
  };

  prototype.scheduleMobileFilterPreview = function scheduleMobileFilterPreview() {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!drawer || drawer.hidden) return;

    window.clearTimeout(this.mobileFilterPreviewTimer);
    this.mobileFilterPreviewController?.abort();
    this.mobilePreviewReady = false;
    this.mobilePreviewError = false;
    this.updateMobileApplyButton(this.mobilePreviewResultCount, { loading: true });

    this.mobileFilterPreviewTimer = window.setTimeout(() => {
      this.previewMobileFilters();
    }, MOBILE_PREVIEW_DELAY);
  };

  prototype.previewMobileFilters = async function previewMobileFilters() {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!drawer || drawer.hidden) return;

    const params = this.paramsFromControls(drawer);
    const requestUrl = this.buildRequestUrl(this.collectionUrl, params);
    const publicUrl = this.buildPublicUrl(params, this.collectionHandle);
    const controller = new AbortController();

    this.mobileFilterPreviewController?.abort();
    this.mobileFilterPreviewController = controller;
    this.mobilePreviewReady = false;
    this.mobilePreviewError = false;
    this.updateMobileApplyButton(this.mobilePreviewResultCount, { loading: true });

    try {
      const html = await this.getSectionHtml(requestUrl, controller.signal);
      if (controller.signal.aborted) return;

      const incoming = this.parseIncoming(html);
      if (!incoming) throw new Error('The collection preview was not returned.');

      const incomingDrawerBody = incoming.querySelector('[data-mobile-filter-drawer] .cae-drawer__body');
      const currentDrawerBody = drawer.querySelector('.cae-drawer__body');
      const incomingProducts = incoming.querySelector('.cae-products');
      const currentProducts = this.querySelector('.cae-products');
      const incomingActiveFilters = incoming.querySelector('[data-active-filters]');
      const currentActiveFilters = this.querySelector('[data-active-filters]');
      const incomingDesktopFilters = incoming.querySelector('[data-desktop-filters]');
      const currentDesktopFilters = this.querySelector('[data-desktop-filters]');
      const incomingMobileTrigger = incoming.querySelector('[data-mobile-filter-open]');
      const currentMobileTrigger = this.querySelector('[data-mobile-filter-open]');

      if (!incomingDrawerBody || !currentDrawerBody || !incomingProducts || !currentProducts) {
        throw new Error('The collection preview is incomplete.');
      }

      const bodyScrollTop = currentDrawerBody.scrollTop;
      const openGroups = new Set(
        [...currentDrawerBody.querySelectorAll('.cae-drawer__group[open]')]
          .map((group) => group.dataset.filterGroup || group.querySelector('summary span')?.textContent?.trim())
          .filter(Boolean)
      );

      currentDrawerBody.innerHTML = incomingDrawerBody.innerHTML;
      currentDrawerBody.querySelectorAll('.cae-drawer__group').forEach((group) => {
        const key = group.dataset.filterGroup || group.querySelector('summary span')?.textContent?.trim();
        if (key && openGroups.has(key)) group.open = true;
      });
      currentDrawerBody.scrollTop = bodyScrollTop;

      currentProducts.replaceWith(incomingProducts.cloneNode(true));
      if (currentActiveFilters && incomingActiveFilters) {
        currentActiveFilters.replaceWith(incomingActiveFilters.cloneNode(true));
      }
      if (currentDesktopFilters && incomingDesktopFilters) {
        currentDesktopFilters.replaceWith(incomingDesktopFilters.cloneNode(true));
      }
      if (currentMobileTrigger && incomingMobileTrigger) {
        currentMobileTrigger.innerHTML = incomingMobileTrigger.innerHTML;
      }

      const resultCount = this.getRenderedResultCount();
      this.mobilePreviewParams = new URLSearchParams(params);
      this.mobilePreviewPublicUrl = publicUrl;
      this.mobilePreviewResultCount = resultCount;
      this.mobilePreviewReady = true;
      this.mobilePreviewError = false;

      this.bindDesktopFilterDropdowns();
      this.updateMobileFilterCount(drawer);
      this.renderMobileSelectedFilters(drawer);
      this.updateMobileApplyButton(resultCount);
      this.announce(Number.isFinite(resultCount)
        ? `${resultCount} matching ${resultCount === 1 ? 'card' : 'cards'}.`
        : 'Matching cards updated.');
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.mobilePreviewReady = false;
      this.mobilePreviewError = true;
      this.updateMobileApplyButton(this.mobilePreviewResultCount, { error: true });
      this.announce('The filter preview could not be updated. Please try again.');
    } finally {
      if (this.mobileFilterPreviewController === controller) {
        this.mobileFilterPreviewController = null;
      }
    }
  };

  prototype.openDrawer = function openDrawer(trigger) {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!drawer) return;

    window.clearTimeout(this.mobileDrawerCloseTimer);
    window.clearTimeout(this.mobileFilterPreviewTimer);
    this.mobileFilterPreviewController?.abort();
    this.drawerTrigger = trigger;
    this.captureMobileDrawerState(drawer);
    this.updateMobileFilterCount(drawer);
    this.renderMobileSelectedFilters(drawer);
    this.updateMobileApplyButton(this.mobilePreviewResultCount);
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cae-filter-sheet-open');
    document.body.classList.add('cae-filter-sheet-open');

    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer.querySelector('[role="dialog"]')?.focus();
    });
  };

  prototype.closeDrawer = function closeDrawer(restoreFocus = true, discardChanges = true) {
    const drawer = this.querySelector('[data-mobile-filter-drawer]');
    if (!drawer || drawer.hidden) return;

    window.clearTimeout(this.mobileFilterPreviewTimer);
    this.mobileFilterPreviewController?.abort();
    this.mobileFilterPreviewController = null;

    if (discardChanges) this.restoreMobileCommittedState();

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cae-filter-sheet-open');
    document.body.classList.remove('cae-filter-sheet-open');

    window.clearTimeout(this.mobileDrawerCloseTimer);
    this.mobileDrawerCloseTimer = window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 220);

    if (restoreFocus) this.drawerTrigger?.focus();
  };

  prototype.commitMobileFilterPreview = function commitMobileFilterPreview() {
    if (!this.mobilePreviewReady || this.mobilePreviewError) {
      this.previewMobileFilters();
      return;
    }

    const params = this.mobilePreviewParams || this.currentParams();
    const publicUrl = this.mobilePreviewPublicUrl || this.buildPublicUrl(params, this.collectionHandle);
    const resultCount = this.mobilePreviewResultCount;

    this.closeDrawer(false, false);
    window.history.pushState({ collectionAjax: true }, '', publicUrl.toString());
    this.currentRenderedParams = new URLSearchParams(params).toString();
    this.mobileCommittedState = null;

    this.announce(Number.isFinite(resultCount)
      ? `${resultCount} ${resultCount === 1 ? 'card' : 'cards'} displayed.`
      : 'Collection results updated.');
    this.setupResponsiveFilters();

    document.dispatchEvent(new CustomEvent('collection:updated', {
      detail: {
        url: publicUrl.toString(),
        collectionHandle: this.collectionHandle
      }
    }));
  };

  prototype.handleClick = function handleClick(event) {
    const mobileClear = event.target.closest('[data-mobile-filter-clear]');
    if (mobileClear && this.contains(mobileClear)) {
      event.preventDefault();
      this.clearMobileDrawerControls();
      return;
    }

    const selectedRemove = event.target.closest('[data-mobile-selected-remove]');
    if (selectedRemove && this.contains(selectedRemove)) {
      event.preventDefault();
      const drawer = selectedRemove.closest('[data-mobile-filter-drawer]');
      if (!drawer) return;

      if (selectedRemove.dataset.mobileSelectedRemove === 'price') {
        drawer.querySelectorAll('[data-collection-filter][type="number"]').forEach((control) => {
          control.value = '';
        });
      } else {
        drawer.querySelectorAll('[data-collection-filter][type="checkbox"]').forEach((control) => {
          if (
            control.name === selectedRemove.dataset.filterParam
            && control.value === selectedRemove.dataset.filterValue
          ) {
            control.checked = false;
          }
        });
      }

      this.updateMobileFilterCount(drawer);
      this.renderMobileSelectedFilters(drawer);
      this.scheduleMobileFilterPreview();
      return;
    }

    const mobileOpen = event.target.closest('[data-mobile-filter-open]');
    if (mobileOpen && this.contains(mobileOpen)) {
      event.preventDefault();
      this.openDrawer(mobileOpen);
      return;
    }

    const mobileClose = event.target.closest('[data-mobile-filter-close]');
    if (mobileClose && this.contains(mobileClose)) {
      event.preventDefault();
      this.closeDrawer(true, true);
      return;
    }

    const mobileApply = event.target.closest('[data-mobile-filter-apply]');
    if (mobileApply && this.contains(mobileApply)) {
      event.preventDefault();
      if (mobileApply.disabled) return;
      this.commitMobileFilterPreview();
      return;
    }

    originalHandleClick.call(this, event);
  };

  prototype.handleChange = function handleChange(event) {
    if (event.target.matches('[data-collection-filter]') && event.target.closest('[data-mobile-filter-drawer]')) {
      this.handleMobileFilterDraftChange(event.target);
      return;
    }

    originalHandleChange.call(this, event);
  };

  prototype.fetchAndReplace = async function fetchAndReplace(...args) {
    const result = await originalFetchAndReplace.apply(this, args);
    document.documentElement.classList.remove('cae-filter-sheet-open');
    document.body.classList.remove('cae-filter-sheet-open');
    this.mobileCommittedState = null;
    this.mobilePreviewReady = true;
    this.mobilePreviewError = false;
    this.setupResponsiveFilters();
    return result;
  };

  document.querySelectorAll('collection-ajax-experience').forEach((element) => {
    element.setupResponsiveFilters();
  });
})();
